import { useRouter } from "next/router";
import {
  getAllCategoriesWithSlug,
  getAllPostsForHome,
  getAllTagsWithSlug,
  getCategoryPostsForSlug,
} from "../../../lib/api";
import { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import Image from "next/image";
import Layout from "../../../components/layout";
import "../../../styles/scss/blog.module.scss";
import dayjs from "dayjs";
import { mwImage } from "../../../lib/common";
import React, { useEffect, useState } from "react";
import Custom404 from "../../404";
import MwBlogs from "../../../components/v2/blog/hero-section_tabs";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";
import {
  getLegacyBlogCategoryRedirectDestination,
  isLegacyBlogCategorySlug,
} from "../../../lib/legacyBlogRedirects";
import BlogCategoryTags from "../../../components/v2/blog/blog-category-tags";
import CallToActionSmall from "../../../components/v2/call-to-action-sm";

export default function Category({ posts, totalPosts, postPerPage, allPosts, topicTags }) {
  const { edges } = allPosts;

  const [postArchive, setPostArchive] = useState([]);
  const router = useRouter();
  const { slug, page } = router.query;
  const pageSlug = typeof slug === "string" && slug;
  const pageNum = typeof page === "string" && page;

  const currentPage = parseInt(pageNum) || 1;
  const totalPages = Math.ceil(totalPosts / postPerPage);

  if (currentPage > totalPages || currentPage < 1) {
    return <Custom404 />;
  }

  let uri = `/blog/category/${pageSlug}/?page=${pageNum}`;
  let nextPagePath = `/blog/category/${pageSlug}/?page=${currentPage + 1}`;
  let prevPagePath = `/blog/category/${pageSlug}/?page=${currentPage - 1}`;

  useEffect(() => {
    let offset = postPerPage * (currentPage - 1);
    let postData = posts?.categories?.edges?.[0]?.node.posts.nodes;
    let __postData = postData?.slice(offset, offset + postPerPage);
    setPostArchive(__postData);
  }, [posts, currentPage, postPerPage]);

  const categoryNode = posts?.categories?.edges?.[0]?.node;
  const categoryName = categoryNode?.name;
  const categoryDescription = categoryNode?.description;
  const categorySeo = categoryNode?.seo;
  const layoutMetaTitle = categorySeo?.title || categoryName;
  const layoutMetaDesc = categorySeo?.metaDesc || categoryDescription;

  const firstTwoPosts = postArchive.slice(0, 3);
  const remainingPosts = postArchive.slice(3);

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };


  return (
    <>
      <Layout
        isHomePage={false}
        pageTitle={layoutMetaTitle}
        ogImage={null}
        uri={uri}
        metaTitle={layoutMetaTitle}
        metaDesc={layoutMetaDesc}
        metaRobotsNoindex={"index"}
        metaRobotsNofollow={"follow"}
      >
        <div className="middle-wrapper site-page blog-v2 category-page">
          <MwBlogs searchInputPostDetails={edges} />
          
          <BlogCategoryTags
            categoryName={categoryName}
            categoryDescription={categoryDescription}
            topicTags={topicTags}
          />

          <div className="container">
            <div className={"top-section"}>
              <div className={"d-flex"}>
                {firstTwoPosts?.map((post, index) => {
                  let link = post.slug;
                  if (post?.parentSlug?.parentSlug != null && post?.parentSlug?.parentSlug.length > 0) {
                    link = post.parentSlug.parentSlug;
                  }
                  return (
                    <>
                      <div className={"category-card"} key={index}>
                        <Link href={`/blog/${link}/`} itemProp="url">
                          {" "}
                          <Image
                            src={mwImage(post?.featuredImage?.node?.sourceUrl)}
                            alt={post?.title}
                            width={400}
                            height={250}
                            loading="lazy"
                            unoptimized
                            sizes="(max-width: 768px) 100vw, 400px"
                          />
                        </Link>
                        <div className={"category knowledge"}>
                          {categoryName}
                        </div>
                        <Link href={`/blog/${link}/`}>
                          <h2>{post?.title}</h2>
                        </Link>
                        <p>{truncateText(post?.subTitle?.subTitle, 117)}</p>
                        <div className={"date-author"}>
                          <p>
                            <Link
                              href={`/blog/authors/${post?.author?.node?.slug}`}
                            >
                              {post?.author?.node?.name}
                            </Link>
                          </p>
                          <p>{dayjs(post?.date).format("MMM D, YYYY")}</p>
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
            </div>

            <div className={"below-section"}>
              <div className={"d-flex"}>
                {remainingPosts?.map((post, index) => {
                  let link = post.slug;
                  if (post?.parentSlug?.parentSlug != null && post?.parentSlug?.parentSlug.length > 0) {
                    link = post.parentSlug.parentSlug;
                  }
                  return (
                    <>
                      <div className={"category-item"} key={index}>
                        <div className={"category knowledge"}>
                          {categoryName}
                        </div>
                        <Link href={`/blog/${link}/`}>
                          <h4>{post.title}</h4>
                        </Link>
                        <div className={"date-author"}>
                          <p>
                            <Link
                              href={`/blog/authors/${post?.author?.node?.slug}`}
                            >
                              {post?.author?.node?.name}
                            </Link>
                          </p>
                          <p>{dayjs(post?.date).format("MMM D, YYYY")}</p>
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
            </div>
          </div>

          <section className="pagination">
            <div className="container">
              <div className="d-flex">
                <div className="pre">
                  {currentPage > 1 && (
                    <>
                      <Link href={prevPagePath}>
                        <Image
                          src={"/images/left-slide.svg"}
                          alt={"Previous Category Page"}
                          width={42}
                          height={32}
                          unoptimized
                        />
                      </Link>
                      <br />
                    </>
                  )}
                </div>
                <div className="next">
                  {currentPage < totalPages && (
                    <Link href={nextPagePath}>
                      <Image
                        src={"/images/right-slide.svg"}
                        alt={"Next Category Page"}
                        width={42}
                        height={32}
                        unoptimized
                      />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
          <CallToActionSmall />
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  locale,
}) => {
  const legacyCategoryDestination = getLegacyBlogCategoryRedirectDestination(
    params?.slug as string | undefined,
  );
  if (legacyCategoryDestination) {
    return {
      redirect: { destination: legacyCategoryDestination, permanent: true },
    };
  }

  const data = await getCategoryPostsForSlug(params?.slug);
  const categoryEdges = data?.categories?.edges;
  if (!categoryEdges?.length) {
    return { notFound: true };
  }

  const categoryNode = categoryEdges[0].node;
  const totalPosts = categoryNode.posts.nodes.length;
  const seo = categoryNode.seo;

  const allPosts = await getAllPostsForHome(preview, locale ?? 'en');
  const allTagsData = await getAllTagsWithSlug();
  const topicTags =
    allTagsData?.edges?.map(({ node }) => ({
      slug: node.slug,
      name: node.name,
    })) ?? [];

  const defaultMetaDesc =
    "Read how observability can scale your cloud infrastructure, best practices, how-tos guide and industry updates from experts.";
  const defaultPageTitle =
    "Observability News, and Best Practices and Guides";

  const resolvedMetaTitle = seo?.title || categoryNode.name || defaultPageTitle;
  const resolvedMetaDesc =
    seo?.metaDesc || categoryNode.description || defaultMetaDesc;

  const uri = `/blog/category/${categoryNode.slug}/`;
  return {
    props: {
      preview,
      posts: data,
      totalPosts,
      allPosts,
      topicTags,
      postPerPage: 8,
      description: resolvedMetaDesc,
      pageTitle: resolvedMetaTitle,
      ogImage:
        categoryNode.posts?.nodes?.[0]?.featuredImage?.node?.sourceUrl || null,
      uri,
      metaTitle: resolvedMetaTitle,
      metaDesc: resolvedMetaDesc,
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
      ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer'], nextI18NextConfig)),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const allCat = await getAllCategoriesWithSlug();

  let paths = [];
  allCat.edges.forEach((item) => {
    if (isLegacyBlogCategorySlug(item.node.slug)) {
      return;
    }
    if (locales) {
      locales.forEach((locale) => {
        paths.push({
          params: { slug: item.node.slug },
          locale,
        });
      });
    } else {
      paths.push({
        params: { slug: item.node.slug },
      });
    }
  });

  return {
    paths: paths,
    fallback: 'blocking',
  };
};
